import { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, startAfter, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { PAGING } from '../config/paging';

/**
 * Infinite scroll helper for loading older user history (beyond a capped realtime listener).
 *
 * - Uses recentOldestDoc (from the live listener) as the starting cursor.
 * - Fetches additional pages with getDocs + startAfter(cursor) + limit(pageSize).
 * - Drives an IntersectionObserver on a sentinel element near the bottom of the list.
 */
export function useInfiniteOlderHistory({ userId, recentOldestDoc, pageSize = PAGING.USER_HISTORY_OLDER_PAGE_SIZE }) {
  const [olderDocs, setOlderDocs] = useState([]); // array of DocumentSnapshots
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);

  const cursorRef = useRef(null); // oldest loaded doc across recent + older
  const sentinelRef = useRef(null);

  // Reset state when user changes
  useEffect(() => {
    setOlderDocs([]);
    setLoadingOlder(false);
    setHasMoreOlder(true);
    cursorRef.current = null;
  }, [userId]);

  // Initialize cursor from the oldest doc in the recent realtime window, if we don't have one yet
  useEffect(() => {
    if (!recentOldestDoc) return;
    if (!cursorRef.current && olderDocs.length === 0) {
      cursorRef.current = recentOldestDoc;
    }
  }, [recentOldestDoc, olderDocs.length]);

  const loadMoreOlder = async () => {
    if (!userId) return;
    if (!hasMoreOlder) return;
    if (loadingOlder) return;
    if (!cursorRef.current) return; // wait until we have a starting point

    try {
      setLoadingOlder(true);

      const baseQuery = query(
        collection(db, 'artHistorySharedArtworks'),
        where('uid', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const pagedQuery = query(baseQuery, startAfter(cursorRef.current), limit(pageSize));
      const snap = await getDocs(pagedQuery);

      if (snap.empty) {
        setHasMoreOlder(false);
        return;
      }

      setOlderDocs((prev) => {
        const existingIds = new Set(prev.map((d) => d.id));
        const nextDocs = [];
        snap.docs.forEach((d) => {
          if (!existingIds.has(d.id)) {
            nextDocs.push(d);
          }
        });
        return [...prev, ...nextDocs];
      });

      // Advance cursor to the oldest doc we've just loaded
      cursorRef.current = snap.docs[snap.docs.length - 1];

      if (snap.docs.length < pageSize) {
        setHasMoreOlder(false);
      }
    } catch (error) {
      console.error('[useInfiniteOlderHistory] Error loading older history:', error);
      // On error, keep hasMoreOlder true so we can retry on next scroll
    } finally {
      setLoadingOlder(false);
    }
  };

  // IntersectionObserver to trigger loading older pages when the sentinel comes into view
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!userId) return;

    const elem = sentinelRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          loadMoreOlder();
        }
      },
      {
        root: null,
        rootMargin: '200px', // start loading a bit before the very bottom
        threshold: 0.1,
      }
    );

    observer.observe(elem);

    return () => {
      observer.unobserve(elem);
      observer.disconnect();
    };
    // We intentionally do NOT include loadMoreOlder in deps to avoid recreating observer too often.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, sentinelRef.current]);

  return {
    olderDocs,
    loadingOlder,
    hasMoreOlder,
    sentinelRef,
  };
}

